import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Archive, Landmark, GitBranch, Layers } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import type { Department } from "../lib/types";
import { PageHeader } from "../components/core/Layout";
import { DataTable } from "../components/core/DataTable";
import { Button } from "../components/core/Button";
import { Modal } from "../components/core/Modal";
import { Field, Input, Select } from "../components/core/Field";
import { StatusBadge } from "../components/core/Badges";

const icons = { department: Landmark, branch: GitBranch, division: Layers };
const empty = { name: "", code: "", unit_type: "department", parent_id: "" };

export default function DepartmentsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const { data, isLoading } = useQuery<Department[]>({ queryKey: ["departments", "mine", showInactive], queryFn: async () => (await api.get("/departments", { params: { include_inactive: showInactive } })).data });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (d: Department) => { setEditing(d); setForm({ name: d.name, code: d.code || "", unit_type: d.unit_type, parent_id: d.parent_id || "" }); setOpen(true); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, code: form.code || null, parent_id: form.parent_id || null };
      if (editing) await api.put(`/departments/${editing.id}`, payload);
      else await api.post("/departments", payload);
      toast.success(editing ? "Updated" : "Created");
      qc.invalidateQueries({ queryKey: ["departments"] });
      setOpen(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (d: Department) => {
    if (!window.confirm(`Archive ${d.name}? Vehicles keep their assignment history.`)) return;
    try {
      await api.delete(`/departments/${d.id}`);
      toast.success("Archived");
      qc.invalidateQueries({ queryKey: ["departments"] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const byId = new Map((data || []).map((d) => [d.id, d]));
  return (
    <div data-testid="departments-page">
      <PageHeader eyebrow="Settings · Organizations" title="Departments, Branches & Divisions" subtitle="Organizational structure for grouping vehicles. Not a permission boundary."
        actions={<><label className="flex items-center gap-1.5 text-xs text-slate-600"><input data-testid="show-inactive-toggle" type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-3.5 w-3.5 accent-[#10046E]" />Show archived</label><Button onClick={openNew} data-testid="create-department-button"><Plus className="h-4 w-4" />New unit</Button></>} />
      <DataTable<Department>
        testId="departments-table" loading={isLoading} rows={data || []} rowKey={(d) => d.id} rowTestId={(d) => `department-row-${d.id}`}
        columns={[
          { key: "name", header: "Unit", render: (d) => { const I = icons[d.unit_type]; return <span className="flex items-center gap-2 font-semibold text-slate-900"><I className="h-4 w-4 text-slate-400" />{d.name}</span>; } },
          { key: "type", header: "Type", render: (d) => <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-slate-600">{d.unit_type}</span> },
          { key: "code", header: "Code", render: (d) => <span className="font-mono text-xs">{d.code || "—"}</span> },
          { key: "parent", header: "Parent", render: (d) => <span className="text-xs">{d.parent_id ? byId.get(d.parent_id)?.name || "—" : <span className="text-slate-400">Top level</span>}</span> },
          { key: "vehicles", header: "Vehicles", className: "text-right", render: (d) => <b>{d.vehicle_count}</b> },
          { key: "status", header: "Status", render: (d) => <StatusBadge status={d.is_active ? "active" : "inactive"} /> },
          { key: "actions", header: "", className: "text-right", render: (d) => <div className="flex justify-end gap-1"><Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-department-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>{d.is_active && <Button size="sm" variant="ghost" onClick={() => archive(d)} data-testid={`archive-department-${d.id}`}><Archive className="h-3.5 w-3.5 text-rose-600" /></Button>}</div> },
        ]}
        empty="No organizational units yet."
      />
      <Modal open={open} onOpenChange={setOpen} title={editing ? `Edit ${editing.name}` : "New organizational unit"} testId="department-form-modal"
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" form="dep-form" loading={busy} data-testid="department-save-button">Save</Button></>}>
        <form id="dep-form" onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label="Name" className="col-span-2"><Input data-testid="department-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="Type"><Select data-testid="department-type-select" value={form.unit_type} onChange={(e) => setForm({ ...form, unit_type: e.target.value })}><option value="department">Department</option><option value="branch">Branch</option><option value="division">Division</option></Select></Field>
          <Field label="Code"><Input data-testid="department-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="font-mono uppercase" /></Field>
          <Field label="Parent unit" className="col-span-2"><Select data-testid="department-parent-select" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}><option value="">Top level</option>{(data || []).filter((d) => d.id !== editing?.id && d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name} ({d.unit_type})</option>)}</Select></Field>
        </form>
      </Modal>
    </div>
  );
}
